import { analytics } from "@/lib/analytics";
import { buildSupportWhatsAppUrl, resolveSupportPage } from "@/lib/support";
import type { SalonStockerHttpTypes } from "@repo/api-sdk";
import { useRouterState } from "@tanstack/react-router";
import type { ComponentPropsWithoutRef, ReactNode } from "react";

type AnchorProps = Omit<
  ComponentPropsWithoutRef<"a">,
  "href" | "target" | "rel" | "onClick"
>;

type Props = AnchorProps & {
  /** `null` when no one is signed in yet (the login page). */
  profile: SalonStockerHttpTypes.StoreProfile | null | undefined;
  /**
   * The page to name in the message and slug in analytics, when it is not the
   * one the router is on (e.g. `/enter` reporting a dead activation link as the
   * activation step). Defaults to the current pathname.
   */
  supportPath?: string;
  children: ReactNode;
};

/**
 * Every link that opens a chat with SalonStocker support on WhatsApp (KAN-13).
 * The prefilled message names the page the salon is on, and the click is
 * tracked with that page's curated key, so each entry point must go through
 * here - a hand-rolled `wa.me` link would skip both.
 */
export function SupportWhatsAppLink({
  profile,
  supportPath,
  children,
  ...anchorProps
}: Props) {
  const routerPathname = useRouterState({
    select: (state) => state.location.pathname
  });
  const pathname = supportPath ?? routerPathname;
  const { key } = resolveSupportPage(pathname);

  return (
    <a
      {...anchorProps}
      href={buildSupportWhatsAppUrl(profile, pathname)}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => analytics.supportWhatsAppOpened({ page: key })}
    >
      {children}
    </a>
  );
}
